import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, AlertTitle } from '@mui/material';

import { useSettings } from '../context/SettingsContext';
import { Nav } from './partials/Nav';
import { API_URL } from './Constants';

const Rates = () => {
  const settings = useSettings();
  const token = settings.userToken;
  const { id } = useParams();
  const navigate = useNavigate();

  const [lot, setLot] = useState(null);
  const [rates, setRates] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    document.title = 'Edit Lot Rates | ValParx Admin';
    if (token) {
      getData();
    }
  }, [token]);

  const getData = () => {
    var myHeaders = new Headers();
    myHeaders.append("vp-session-token", token);

    var formdata = new FormData();
    formdata.append("id", id);

    fetch(API_URL + "getlotdata", {
      method: 'POST',
      headers: myHeaders,
      body: formdata
    })
      .then(response => response.json())
      .then(result => {
        if (result.code === 200) {
          setLot(result.lot);
          setRates(result.lot.rates || []);
        }
      })
      .catch(error => setError('Error loading lot data'));
  };

  const addRate = () => {
    setRates([...rates, {
      start_minutes: rates.length > 0 ? rates[rates.length - 1].end_minutes : 0,
      end_minutes: 0,
      rate_amount: 0
    }]);
  };

  const removeRate = (index) => {
    const newRates = [...rates];
    newRates.splice(index, 1);
    setRates(newRates);
  };

  const updateRate = (index, field, value) => {
    const newRates = [...rates];
    newRates[index][field] = value;
    setRates(newRates);
  };

  const validateRates = () => {
    if (rates.length === 0) {
      setError('At least one rate is required');
      return false;
    }

    for (let i = 0; i < rates.length; i++) {
      const rate = rates[i];
      
      if (rate.start_minutes < 0 || rate.end_minutes < 0 || rate.rate_amount < 0) {
        setError('Values cannot be negative');
        return false;
      }

      if (rate.end_minutes !== 0 && rate.end_minutes <= rate.start_minutes) {
        setError('End minutes must be greater than start minutes');
        return false;
      }

      if (i > 0 && rate.start_minutes !== rates[i-1].end_minutes) {
        setError('Time ranges must be continuous. In other words, if one rate ends at 60, the next should start at 60.');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!validateRates()) {
      return;
    }

    setIsSubmitting(true);

    var myHeaders = new Headers();
    myHeaders.append("vp-session-token", token);

    var formdata = new FormData();
    formdata.append("id", id);
    formdata.append("rates", JSON.stringify(rates));

    fetch(API_URL + "updatelotrates", {
      method: 'POST',
      headers: myHeaders,
      body: formdata
    })
      .then(response => response.json())
      .then(result => {
        setIsSubmitting(false);
        if (result.code === 200) {
          setSuccess(true);
          setTimeout(() => navigate(`/lots/${id}`), 2000);
        } else {
          setError(result.message || 'Error updating rates');
        }
      })
      .catch(error => {
        setIsSubmitting(false);
        setError('Error updating rates');
      });
  };

  return (
    <div className='min-h-screen flex items-start justify-center pt-[63px]'>
      <Nav />

      <div className='w-full'>
        <div className="max-w-4xl mx-auto w-full pt-20">
          {lot ? (
            <>
              <div className='mb-12'>
                <h2 className='mb-0'>Edit Rates for {lot.title}</h2>
              </div>

              {error && (
                <Alert severity="error" className='mb-4'>
                  <AlertTitle>Error</AlertTitle>
                  {error}
                </Alert>
              )}

              {success && (
                <Alert severity="success" className='mb-4'>
                  <AlertTitle>Success</AlertTitle>
                  Rates updated successfully! Redirecting...
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                <div className='p-12 pb-8 bg-secondary rounded-xl mb-12'>
                  {rates.map((rate, index) => (
                    <div key={index} className='grid grid-cols-4 gap-4 mb-6'>
                      <div>
                        <label className='block text-sm font-medium text-gray-700 mb-1'>
                          Start (minutes)
                        </label>
                        <input
                          type="number"
                          value={rate.start_minutes}
                          onChange={(e) => updateRate(index, 'start_minutes', parseInt(e.target.value))}
                          className="text-2xl border border-solid border-accent w-full p-1"
                          min="0"
                          required
                        />
                      </div>
                      <div>
                        <label className='block text-sm font-medium text-gray-700 mb-1'>
                          End (minutes, 0 for unlimited)
                        </label>
                        <input
                          type="number"
                          value={rate.end_minutes}
                          onChange={(e) => updateRate(index, 'end_minutes', parseInt(e.target.value))}
                          className="text-2xl border border-solid border-accent w-full p-1"
                          min="0"
                          required
                        />
                      </div>
                      <div>
                        <label className='block text-sm font-medium text-gray-700 mb-1'>
                          Rate Amount ($)
                        </label>
                        <input
                          type="number"
                          value={rate.rate_amount}
                          onChange={(e) => updateRate(index, 'rate_amount', parseFloat(e.target.value))}
                          className="text-2xl border border-solid border-accent w-full p-1"
                          min="0"
                          step="0.01"
                          required
                        />
                      </div>
                      <div className='flex items-end'>
                        <button
                          type="button"
                          onClick={() => removeRate(index)}
                          className='text-red-600 hover:text-red-800'
                        >
                          <FontAwesomeIcon icon="fa-solid fa-trash" />
                        </button>
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={addRate}
                    className='text-accent hover:text-accent-dark mb-4'
                  >
                    <FontAwesomeIcon icon="fa-solid fa-plus" className='mr-2' />
                    Add Rate Range
                  </button>
                </div>

                <div className='flex justify-end gap-4'>
                  <button
                    type="button"
                    onClick={() => navigate(`/lots/${id}`)}
                    className='py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className='py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-accent hover:bg-accent-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                  >
                    {isSubmitting ? (
                      <FontAwesomeIcon icon="fa-solid fa-spinner" className='fa-spin' />
                    ) : (
                      'Save Changes'
                    )}
                  </button>
                </div>
              </form>
            </>
          ) : (
            <div className='text-blueGray-500 items-center flex flex-col text-center justify-center w-full h-screen'>
              <p className='text-4xl font-bold mb-6'>Loading lot data...</p>
              <div><FontAwesomeIcon icon="fa-solid fa-spinner" className='text-5xl ml-2 fa-spin' /></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Rates;
